import type { Point, SceneNode } from "../scene/model";
import {
  invertAffine,
  transformPoint,
  type AffineMatrix,
} from "../geometry/affine";

const radians = Math.PI / 180;
export function multiply(a: AffineMatrix, b: AffineMatrix): AffineMatrix {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5],
  ];
}
/** Centered unit square -> document. Geometry owns size; transform owns angle/shear. */
export function nodeMatrix(node: SceneNode): AffineMatrix {
  const angle = (node.transform.rotation || 0) * radians,
    c = Math.cos(angle),
    s = Math.sin(angle),
    k = Math.tan((node.transform.skewX || 0) * radians),
    w = node.geometry.width!,
    h = node.geometry.height!;
  return [
    c * w,
    s * w,
    (c * k - s) * h,
    (s * k + c) * h,
    node.transform.x!,
    node.transform.y!,
  ];
}
export const unitCorners: readonly Point[] = [
  { x: -0.5, y: -0.5 },
  { x: 0.5, y: -0.5 },
  { x: 0.5, y: 0.5 },
  { x: -0.5, y: 0.5 },
];
export function corners(matrix: AffineMatrix) {
  return unitCorners.map((p) => transformPoint(p, matrix));
}
export function normalizeAngle(angle: number) {
  return ((angle % 360) + 360) % 360;
}
export function rotatedAround(center: Point, angle: number): AffineMatrix {
  const c = Math.cos(angle * radians),
    s = Math.sin(angle * radians);
  return [
    c,
    s,
    -s,
    c,
    center.x - c * center.x + s * center.y,
    center.y - s * center.x - c * center.y,
  ];
}
/** QR decomposition bakes positive dimensions and preserves every world corner. */
export function transformNativeNode(
  node: SceneNode,
  matrix: AffineMatrix,
): SceneNode {
  const m = multiply(matrix, nodeMatrix(node)),
    width = Math.hypot(m[0], m[1]),
    height = (m[0] * m[3] - m[1] * m[2]) / width,
    shear = (m[0] * m[2] + m[1] * m[3]) / (width * height);
  if (
    ![...m, width, height, shear].every(Number.isFinite) ||
    width < 1 - 1e-8 ||
    height < 1 - 1e-8 ||
    width > 1e6 ||
    height > 1e6 ||
    Math.abs(m[4]) > 1e6 ||
    Math.abs(m[5]) > 1e6 ||
    Math.abs(shear) > 1e5
  )
    throw new Error("Преобразование выходит за допустимые размеры.");
  return {
    ...node,
    geometry: {
      ...node.geometry,
      width: Math.max(1, width),
      height: Math.max(1, height),
    },
    transform: {
      ...node.transform,
      x: m[4],
      y: m[5],
      rotation: normalizeAngle(Math.atan2(m[1], m[0]) / radians),
      skewX: Math.atan(shear) / radians,
    },
  };
}
export function resizeMatrix(
  frame: AffineMatrix,
  handle: readonly [number, number],
  delta: Point,
  free: boolean,
  centered: boolean,
  minimum = { x: 0.001, y: 0.001 },
): AffineMatrix {
  const inverse = invertAffine(frame)!;
  const dx = inverse[0] * delta.x + inverse[2] * delta.y,
    dy = inverse[1] * delta.x + inverse[3] * delta.y,
    [sx, sy] = handle,
    factor = centered ? 2 : 1;
  let x = sx ? Math.max(minimum.x, 1 + dx * sx * factor) : 1,
    y = sy ? Math.max(minimum.y, 1 + dy * sy * factor) : 1;
  if (sx && sy && !free) x = y = Math.max(x, y, minimum.x, minimum.y);
  const ax = centered ? 0 : -sx / 2,
    ay = centered ? 0 : -sy / 2;
  return multiply(
    multiply(frame, [x, 0, 0, y, ax * (1 - x), ay * (1 - y)]),
    inverse,
  );
}
export function rotationMatrix(
  frame: AffineMatrix,
  start: Point,
  end: Point,
  free: boolean,
) {
  const center = { x: frame[4], y: frame[5] },
    initial = Math.atan2(frame[1], frame[0]) / radians;
  let delta =
    (Math.atan2(end.y - center.y, end.x - center.x) -
      Math.atan2(start.y - center.y, start.x - center.x)) /
    radians;
  if (!free) delta = Math.round((initial + delta) / 5) * 5 - initial;
  return rotatedAround(center, delta);
}
